import { t } from '@/i18n';
import { DateRangeSchema, isRS, sortOrderType } from '@/utils';
import { z } from 'zod';
import { NAME_MAX_LENGTH } from '../utils';
import { AccountingTypeModel } from './accounting-type';

const ExpenditurePurposeLevel = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

export const ExpenditurePurposeLevelLabelModifier = {
  1: t('expenditure-purpose-level-1'),
  2: t('expenditure-purpose-level-2'),
  3: t('expenditure-purpose-level-3'),
} as const satisfies Record<z.infer<typeof ExpenditurePurposeLevel>, string>;

const ExpenditurePurposeBaseModel = z.object({
  id: z.string().uuid(),
  code: z.number().int().min(1),
  name: z.string().min(1),
  level: ExpenditurePurposeLevel,
  upperId: z.string().uuid().nullable(),
  accountingId: z.string().uuid().nullish(),
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  isMajorMatter: z.boolean().nullish(),
  sortOrder: isRS() ? sortOrderType.max(99999) : sortOrderType.nullish(),
  ...(isRS() ? {} : { budgetAgendaSortOrder: sortOrderType.nullable() }),
});

type RecursiveExpenditurePurpose = z.infer<
  typeof ExpenditurePurposeBaseModel
> & {
  upper: RecursiveExpenditurePurpose | null;
};

export const ExpenditurePurposesModel = ExpenditurePurposeBaseModel.extend({
  accounting: AccountingTypeModel.nullable(),
}).array();

const RecursiveExpenditurePurposeModel: z.ZodType<RecursiveExpenditurePurpose> =
  ExpenditurePurposeBaseModel.extend({
    upper: z.lazy(() => RecursiveExpenditurePurposeModel.nullable()),
  });

export const ExpenditurePurposeModel = ExpenditurePurposeBaseModel.extend({
  upper: z.lazy(() => RecursiveExpenditurePurposeModel.nullable()),
  accounting: AccountingTypeModel.nullable(),
});

export type ExpenditurePurposeModel = z.infer<typeof ExpenditurePurposeModel>;

const ExpenditurePurposeFormBaseSchema = ExpenditurePurposeBaseModel.pick({
  code: true,
  accountingId: true,
  isMajorMatter: true,
  sortOrder: true,
}).extend({
  name: ExpenditurePurposeBaseModel.shape.name.max(NAME_MAX_LENGTH),
  level: z.number().int().min(1).max(ExpenditurePurposeLevel.options.length),
  period: DateRangeSchema.nullish(),
  upperIds: ExpenditurePurposeBaseModel.shape.upperId
    .unwrap()
    .array()
    .optional(),
});

export const ExpenditurePurposeFormSchema =
  ExpenditurePurposeFormBaseSchema.refine(
    (data) => !(data.level === 1 && data.upperIds && data.upperIds.length > 0),
    {
      path: ['upperIds'],
      message: t('unreachable-code'),
    },
  )
    .refine((data) => !(data.level !== 1 && !data.upperIds?.length), {
      path: ['upperIds'],
      message: t('validation.no-upper-selected'),
    })
    .refine((data) => data.level !== 1 || data.accountingId, {
      path: ['accountingId'],
      message: t('validation.required'),
    });

export const UpdateExpenditurePurposeParamsSchema =
  ExpenditurePurposeFormBaseSchema.extend({
    id: z.string().uuid(),
  });

export const UpdateExpenditurePurposeBudgetAgendaSortOrderParamsSchema = z
  .object({
    id: ExpenditurePurposeBaseModel.shape.id,
    budgetAgendaSortOrder: sortOrderType,
  })
  .array();
